<template>
  <div class="flex flex-col items-center w-full">
    <!-- Chord name -->
    <div class="text-sm font-bold mb-2 text-gray-900 dark:text-white">{{ chordName }}</div>

    <!-- SVG diagram -->
    <svg
      width="114"
      height="52"
      viewBox="0 0 114 52"
      xmlns="http://www.w3.org/2000/svg"
      class="overflow-visible select-none"
    >
      <!-- White Keys -->
      <g>
        <rect
          v-for="wk in whiteKeys"
          :key="'wk-' + wk.midi"
          :x="wk.x"
          y="1"
          width="15"
          height="50"
          rx="1.5"
          :class="[
            isBass(wk.midi)
              ? 'fill-emerald-500 stroke-emerald-600 dark:fill-emerald-600 dark:stroke-emerald-700'
              : isPressed(wk.midi)
                ? 'fill-indigo-500 stroke-indigo-600 dark:fill-indigo-600 dark:stroke-indigo-700'
                : 'fill-white stroke-gray-300 dark:fill-slate-700 dark:stroke-slate-600'
          ]"
          stroke-width="1.5"
        />
      </g>

      <!-- Black Keys -->
      <g>
        <rect
          v-for="bk in blackKeys"
          :key="'bk-' + bk.midi"
          :x="bk.x"
          y="1"
          width="9"
          height="32"
          rx="1"
          :class="[
            isBass(bk.midi)
              ? 'fill-emerald-400 stroke-emerald-500 dark:fill-emerald-500 dark:stroke-emerald-600'
              : isPressed(bk.midi)
                ? 'fill-indigo-400 stroke-indigo-500 dark:fill-indigo-500 dark:stroke-indigo-600'
                : 'fill-slate-900 stroke-slate-950 dark:fill-slate-950 dark:stroke-black'
          ]"
          stroke-width="1"
        />
      </g>
    </svg>
  </div>
</template>

<script setup>
import { defineProps, computed } from 'vue'

const props = defineProps({
  chordName: { type: String, required: true },
  position: { type: Object, required: true }
})

// Define the 1-octave keyboard mapping (MIDI 60 to 71)
const whiteKeys = [
  { midi: 60, x: 1 },   // C
  { midi: 62, x: 17 },  // D
  { midi: 64, x: 33 },  // E
  { midi: 65, x: 49 },  // F
  { midi: 67, x: 65 },  // G
  { midi: 69, x: 81 },  // A
  { midi: 71, x: 97 }   // B
]

const blackKeys = [
  { midi: 61, x: 11.5 }, // C#
  { midi: 63, x: 27.5 }, // D#
  { midi: 66, x: 59.5 }, // F#
  { midi: 68, x: 75.5 }, // G#
  { midi: 70, x: 91.5 }  // A#
]

// Extract bass note pitch from slash chords: "A/B" -> "B"
const bassMidi = computed(() => {
  const slashIdx = props.chordName.indexOf('/')
  if (slashIdx === -1) return null
  const bassName = props.chordName.slice(slashIdx + 1).trim()
  const pitchMap = {
    'C': 0, 'C#': 1, 'Db': 1, 'D': 2, 'D#': 3, 'Eb': 3,
    'E': 4, 'F': 5, 'F#': 6, 'Gb': 6, 'G': 7, 'G#': 8, 'Ab': 8,
    'A': 9, 'A#': 10, 'Bb': 10, 'B': 11
  }
  const pc = pitchMap[bassName]
  return pc !== undefined ? 60 + pc : null
})

const isBass = (midi) => {
  return bassMidi.value === midi
}

const isPressed = (midi) => {
  if (!props.position?.midi) return false
  // Normalize MIDI notes to 60-71 range so they fit our 1-octave keyboard representation
  const normalized = props.position.midi.map(m => ((m - 60) % 12 + 12) % 12 + 60)
  return normalized.includes(midi)
}
</script>

<style scoped>
svg rect {
  transition: fill 0.15s ease, stroke 0.15s ease;
}
</style>
